import { create } from 'zustand';
import * as Y from 'yjs';
import {
  type ScenarioId,
  type NodeId,
  type ManagerId,
  type ScenarioDelta,
  type NodeAddition,
  type NodeRemoval,
  type NodeMove,
  type NodeUpdate,
  type NodeUpdateChange,
  type ScenarioAggregateContribution,
  createScenarioId,
  createNodeId,
  createManagerId,
  createEmptyDelta,
} from '../../data-profiles/scenarios/ScenarioDelta';
import {
  type ScenarioLifecycleState,
  type ScenarioStatusIndex,
  type ActionResult,
  buildStatusIndex,
  canTransition,
  success,
  successVoid,
  failure,
  CreateScenarioSchema,
} from '../../data-profiles/scenarios/ScenarioModel';
import {
  type CommandDeltaContribution,
  mergeDeltas,
  unmergeDeltas,
} from '../../data-profiles/scenarios/ScenarioRecordable';
import { DEFAULT_ORG_PERSON_NODE_TYPE } from '../../../domains/orgchart/models/defaults';

// =============================================================================
// Legacy Change Type (for backward compatibility)
// =============================================================================

export type ScenarioChangeType = 'add' | 'remove' | 'move' | 'modify';

export interface ScenarioChange {
  id: string;
  type: ScenarioChangeType;
  nodeId: string;
  description: string;
  data?: Record<string, unknown>;
  createdAt: Date;
}

// =============================================================================
// Scenario Interface (Enhanced with Delta)
// =============================================================================

export interface Scenario {
  id: ScenarioId;
  name: string;
  description?: string;
  /** @deprecated Use delta instead. Kept for backward compatibility. */
  changes: ScenarioChange[];
  /** Delta-based change tracking */
  delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>;
  /** A durable dashboard proposal retains its visual overlay but is immutable. */
  submissionState?: 'submitted';
  /** Server identity used to reconcile dashboard apply/discard/rebase safely. */
  submittedScenarioId?: string;
  submittedChangeSetId?: string;
  /** Operational review that owns this read-only canvas delta until terminal settlement. */
  submittedOperationalChangeSetId?: string;
  /** Pre-computed status index for O(1) lookups */
  statusIndex: ScenarioStatusIndex;
  /** Base snapshot hash for conflict detection */
  baseSnapshotHash: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// =============================================================================
// Yjs Storage Helpers
// =============================================================================

interface StoredScenario {
  id: string;
  name: string;
  description?: string;
  changes: Array<Omit<ScenarioChange, 'createdAt'> & { createdAt: string }>;
  delta?: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>;
  submissionState?: 'submitted';
  submittedScenarioId?: string;
  submittedChangeSetId?: string;
  submittedOperationalChangeSetId?: string;
  baseSnapshotHash?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

const scenarioContributionMapName = (scenarioId: string): string =>
  `scenario-contributions:${scenarioId}`;

export const getScenarioContributionMap = (
  doc: Y.Doc,
  scenarioId: string,
): Y.Map<ScenarioAggregateContribution> =>
  doc.getMap<ScenarioAggregateContribution>(scenarioContributionMapName(scenarioId));

export const loadScenarioAggregateContributions = (
  doc: Y.Doc,
  scenarioId: string,
): ScenarioAggregateContribution[] => {
  const contributions: ScenarioAggregateContribution[] = [];
  getScenarioContributionMap(doc, scenarioId).forEach((value, aggregateKey) => {
    if (value && typeof value === 'object') contributions.push({ ...value, aggregateKey });
  });
  return contributions.sort((left, right) => left.aggregateKey.localeCompare(right.aggregateKey));
};

function edgeRelationType(edge: { type: string; props?: Record<string, unknown> }): string {
  return String(edge.props?.relation ?? edge.type);
}

/** Compose independently owned aggregate fragments over a legacy base delta. */
export const composeScenarioAggregateContributions = (
  baseDelta: ScenarioDelta,
  contributions: readonly ScenarioAggregateContribution[],
): ScenarioDelta => {
  let composed: ScenarioDelta = {
    additions: [...baseDelta.additions],
    removals: [...baseDelta.removals],
    moves: [...baseDelta.moves],
    updates: [...baseDelta.updates],
    edgeAdditions: [...baseDelta.edgeAdditions],
    edgeRemovals: [...baseDelta.edgeRemovals],
  };

  const sortedContributions = [...contributions].sort((left, right) =>
    left.aggregateKey.localeCompare(right.aggregateKey));

  // Remove aggregate-owned operations from the legacy base first. Aggregate
  // fragments are additive to each other; one aggregate must never erase a
  // different aggregate merely because their migration fields overlap.
  for (const contribution of sortedContributions) {
    const nodeIds = new Set((contribution.ownership.nodeIds ?? []).map(String));
    const edgeIds = new Set((contribution.ownership.edgeIds ?? []).map(String));
    const ownedFields = new Map(
      Object.entries(contribution.ownership.nodeFields ?? {}).map(([nodeId, fields]) =>
        [nodeId, new Set(fields)] as const),
    );
    const edgeTargets = contribution.ownership.edgeTargets ?? [];
    const ownsEdgeTarget = (edge: { to: NodeId; type: string; props?: Record<string, unknown> }) =>
      edgeTargets.some((owned) =>
        String(owned.targetId) === String(edge.to)
        && owned.relationTypes.includes(edgeRelationType(edge)));

    composed = {
      additions: composed.additions.filter((entry) => !nodeIds.has(String(entry.id))),
      removals: composed.removals.filter((entry) => !nodeIds.has(String(entry.nodeId))),
      moves: composed.moves.filter((entry) => !nodeIds.has(String(entry.nodeId))),
      updates: composed.updates.filter((entry) => {
        const fields = ownedFields.get(String(entry.nodeId));
        return !fields?.has(entry.change.field);
      }),
      edgeAdditions: composed.edgeAdditions.filter((entry) =>
        !edgeIds.has(String(entry.id)) && !ownsEdgeTarget(entry)),
      edgeRemovals: composed.edgeRemovals.filter((entry) => !edgeIds.has(String(entry.edgeId))),
    };

  }
  for (const contribution of sortedContributions) {
    composed.additions.push(...contribution.delta.additions);
    composed.removals.push(...contribution.delta.removals);
    composed.moves.push(...contribution.delta.moves);
    composed.updates.push(...contribution.delta.updates);
    composed.edgeAdditions.push(...contribution.delta.edgeAdditions);
    composed.edgeRemovals.push(...contribution.delta.edgeRemovals);
  }
  return composed;
};

/**
 * Load scenarios from Y.Doc's scenarios map
 */
export const loadScenariosFromYDoc = (doc: Y.Doc): Scenario[] => {
  try {
    const scenariosMap = doc.getMap('scenarios');
    const scenarios: Scenario[] = [];

    scenariosMap.forEach((value, key) => {
      if (value && typeof value === 'object') {
        const s = value as StoredScenario;
        const baseDelta = s.delta ?? createEmptyDelta();
        const composedDelta = composeScenarioAggregateContributions(
          baseDelta,
          loadScenarioAggregateContributions(doc, s.id || key),
        );
        const delta = removePresentationOnlyUpdates(composedDelta);
        const rawChanges = s.changes || [];
        const cleanedChanges = rawChanges.filter((c) => !isPresentationOnlyScenarioField(c.data?.field));
        // A scenario created only by the old custom-field path is not a real
        // planning draft. Drop it from the hydrated store; ordinary empty
        // scenarios remain valid and continue to restore normally.
        if ((rawChanges.length > cleanedChanges.length || composedDelta.updates.length !== delta.updates.length)
          && !hasScenarioChanges(delta, cleanedChanges)) return;
        scenarios.push({
          id: createScenarioId(s.id || key),
          name: s.name || 'Unnamed',
          description: s.description,
          changes: cleanedChanges
            .map((c) => ({ ...c, createdAt: new Date(c.createdAt) })),
          delta,
          submissionState: s.submissionState,
          submittedScenarioId: s.submittedScenarioId,
          submittedChangeSetId: s.submittedChangeSetId,
          submittedOperationalChangeSetId: s.submittedOperationalChangeSetId,
          statusIndex: buildStatusIndex(delta),
          baseSnapshotHash: s.baseSnapshotHash ?? '',
          createdAt: new Date(s.createdAt || Date.now()),
          updatedAt: new Date(s.updatedAt || Date.now()),
          createdBy: s.createdBy ?? 'unknown',
        });
      }
    });

    return scenarios;
  } catch (e) {
    console.error('[ScenariosStore] Failed to load scenarios from YDoc:', e);
    return [];
  }
};

function isPresentationOnlyScenarioField(field: unknown): boolean {
  return typeof field === 'string' && (field === 'customFieldOverrides' || field.startsWith('customFieldOverrides.'));
}

function removePresentationOnlyUpdates(delta: Scenario['delta']): Scenario['delta'] {
  const updates = delta.updates.filter((update) => !isPresentationOnlyScenarioField(update.change.field));
  return updates.length === delta.updates.length ? delta : { ...delta, updates };
}

function hasScenarioChanges(delta: Scenario['delta'], changes: readonly unknown[]): boolean {
  return changes.length > 0
    || delta.additions.length > 0
    || delta.removals.length > 0
    || delta.moves.length > 0
    || delta.updates.length > 0
    || delta.edgeAdditions.length > 0
    || delta.edgeRemovals.length > 0;
}

/**
 * Save a single scenario to Y.Doc's scenarios map
 */
export const saveScenarioToYDoc = (doc: Y.Doc, scenario: Scenario): boolean => {
  try {
    const scenariosMap = doc.getMap('scenarios');
    const persisted = scenariosMap.get(scenario.id as string) as StoredScenario | undefined;
    const hasAggregateContributions = getScenarioContributionMap(doc, scenario.id as string).size > 0;
    const toStore: StoredScenario = {
      id: scenario.id as string,
      name: scenario.name,
      description: scenario.description,
      changes: scenario.changes.map((c) => ({
        ...c,
        createdAt: c.createdAt.toISOString(),
      })),
      // A composed delta is a read model. Do not fold aggregate fragments back
      // into the legacy base value during metadata-only saves.
      delta: hasAggregateContributions ? (persisted?.delta ?? createEmptyDelta()) : scenario.delta,
      submissionState: scenario.submissionState,
      submittedScenarioId: scenario.submittedScenarioId,
      submittedChangeSetId: scenario.submittedChangeSetId,
      submittedOperationalChangeSetId: scenario.submittedOperationalChangeSetId,
      baseSnapshotHash: scenario.baseSnapshotHash,
      createdAt: scenario.createdAt.toISOString(),
      updatedAt: scenario.updatedAt.toISOString(),
      createdBy: scenario.createdBy,
    };

    doc.transact(() => {
      scenariosMap.set(scenario.id as string, toStore);
    }, 'scenario-save');

    return true;
  } catch (e) {
    console.error('[ScenariosStore] Failed to save scenario to YDoc:', e);
    return false;
  }
};

/**
 * Delete a scenario from Y.Doc's scenarios map
 */
const deleteScenarioFromYDoc = (doc: Y.Doc, scenarioId: ScenarioId): boolean => {
  try {
    const scenariosMap = doc.getMap('scenarios');
    const contributionsMap = getScenarioContributionMap(doc, scenarioId as string);
    doc.transact(() => {
      scenariosMap.delete(scenarioId as string);
      contributionsMap.clear();
    }, 'scenario-delete');
    return true;
  } catch (e) {
    console.error('[ScenariosStore] Failed to delete scenario from YDoc:', e);
    return false;
  }
};

// =============================================================================
// Store State Interface
// =============================================================================

interface ScenariosState {
  diagramId: string | null;
  /** Reference to the Y.Doc for collaborative storage */
  ydoc: Y.Doc | null;
  scenarios: Scenario[];
  /** Map for O(1) scenario lookups */
  scenarioById: Map<ScenarioId, Scenario>;
  activeScenarioId: ScenarioId | null;
  /** Request token for race condition prevention */
  activeScenarioRequestToken: symbol | null;
  /** Lifecycle state machine */
  scenarioState: ScenarioLifecycleState;
  /** Explicit Manual/AI scenario authoring differs from an implicit planning draft. */
  connectorAuthoringMode: 'matrix_reporting' | 'primary_reporting';
  isLoading: boolean;
  error: string | null;
}

interface ScenariosActions {
  // Initialization
  initialize: (diagramId: string, ydoc: Y.Doc | null) => void;

  // CRUD operations
  createScenario: (name: string, description?: string) => Scenario;
  renameScenario: (id: string, name: string) => void;
  deleteScenario: (id: string) => void;

  // State transitions
  setActiveScenario: (id: string | null) => symbol;
  enterEditMode: () => ActionResult;
  exitEditMode: () => ActionResult;
  setConnectorAuthoringMode: (mode: 'matrix_reporting' | 'primary_reporting') => void;

  // Delta mutations
  addNodeToDelta: (node: NodeAddition) => ActionResult<NodeId>;
  removeNodeFromDelta: (nodeId: NodeId, originalData: NodeRemoval['originalData']) => ActionResult;
  moveNodeInDelta: (nodeId: NodeId, oldManagerId: ManagerId | null, newManagerId: ManagerId | null) => ActionResult;
  updateNodeInDelta: (nodeId: NodeId, change: NodeUpdateChange) => ActionResult;
  replaceActiveScenarioDelta: (
    delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>
  ) => ActionResult;
  upsertActiveScenarioAggregate: (contribution: ScenarioAggregateContribution) => ActionResult;
  removeActiveScenarioAggregate: (aggregateKey: string) => ActionResult;
  getActiveScenarioAggregate: (aggregateKey: string) => ScenarioAggregateContribution | null;
  undoLastDeltaChange: () => ActionResult;
  applyDeltaContributionToActiveScenario: (
    contribution: CommandDeltaContribution<Record<string, unknown>, unknown>
  ) => ActionResult;
  removeDeltaContributionFromActiveScenario: (
    contribution: CommandDeltaContribution<Record<string, unknown>, unknown>
  ) => ActionResult;

  // Legacy change API (backward compatibility)
  addChange: (scenarioId: string, change: Omit<ScenarioChange, 'id' | 'createdAt'>) => void;
  removeChange: (scenarioId: string, changeId: string) => void;

  // Queries
  getScenarios: () => Scenario[];
  getScenario: (id: string) => Scenario | undefined;
  getActiveScenario: () => Scenario | null;
  getStatusIndex: () => ScenarioStatusIndex | null;

  // Apply/Discard
  applyScenario: (id: string) => ScenarioChange[];
  /** Makes a staged dashboard proposal read-only without clearing its canvas overlay. */
  markScenarioSubmitted: (
    id: string,
    expectedUpdatedAt: string,
    serverDraft: { scenarioId: string; changeSetId: string; operationalChangeSetId?: string },
  ) => boolean;
  completeAppliedScenario: (id: string, expectedUpdatedAt: string) => boolean;
  discardScenario: (id: string) => void;
}

export type ScenariosStore = ScenariosState & ScenariosActions;

let detachScenarioObservers: (() => void) | null = null;

/**
 * Tab-local memory of the active scenario per diagram. The scenario DATA
 * survives a reload inside the Y.Doc, but this store's selection did not —
 * so after F5 the chart snapped back to the live view and the user's draft
 * "disappeared" (QA issue 78). Session-scoped on purpose: a refresh restores
 * the same tab's draft, while a collaborator opening the chart fresh still
 * lands on the live view.
 */
const ACTIVE_SCENARIO_STORAGE_PREFIX = 'creately-active-scenario:';

function rememberActiveScenario(diagramId: string | null, scenarioId: string | null): void {
  if (!diagramId || typeof sessionStorage === 'undefined') return;
  try {
    if (scenarioId) sessionStorage.setItem(`${ACTIVE_SCENARIO_STORAGE_PREFIX}${diagramId}`, scenarioId);
    else sessionStorage.removeItem(`${ACTIVE_SCENARIO_STORAGE_PREFIX}${diagramId}`);
  } catch {
    // Storage unavailable (private/sandboxed context) — restore is best-effort.
  }
}

function recallActiveScenario(diagramId: string | null): string | null {
  if (!diagramId || typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(`${ACTIVE_SCENARIO_STORAGE_PREFIX}${diagramId}`);
  } catch {
    return null;
  }
}

/**
 * The draft a fresh page load should show: this tab's last active scenario,
 * else the latest submitted proposal, whose read-only overlay keeps explaining
 * the proposal when the planning chart is reopened.
 */
function chooseRestoredScenarioId(
  diagramId: string | null,
  scenarios: readonly Scenario[],
  scenarioById: ReadonlyMap<ScenarioId, Scenario>,
): ScenarioId | null {
  const recalledId = recallActiveScenario(diagramId);
  if (recalledId && scenarioById.has(createScenarioId(recalledId))) return createScenarioId(recalledId);
  return [...scenarios]
    .filter((scenario) => scenario.submissionState === 'submitted')
    .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime())[0]?.id ?? null;
}

/**
 * Diagram whose restore still waits for its scenarios. A reload can
 * initialize the store before the Y.Doc has synced, when the doc holds no
 * scenarios yet; the restore then runs once they arrive, unless the person
 * has chosen a scenario (or the live view) in the meantime.
 */
let restorePendingForDiagramId: string | null = null;

// =============================================================================
// Store Implementation
// =============================================================================

export const useScenariosStore = create<ScenariosStore>((set, get) => ({
  // State
  diagramId: null,
  ydoc: null,
  scenarios: [],
  scenarioById: new Map(),
  activeScenarioId: null,
  activeScenarioRequestToken: null,
  scenarioState: 'VIEWING_LIVE',
  connectorAuthoringMode: 'matrix_reporting',
  isLoading: false,
  error: null,

  // Initialization
  initialize: (diagramId, ydoc) => {
    const { diagramId: currentDiagramId, activeScenarioId: currentActiveId, activeScenarioRequestToken: currentToken, scenarioState: currentState } = get();

    // Load scenarios from Y.Doc if available
    const scenarios = ydoc ? loadScenariosFromYDoc(ydoc) : [];
    const scenarioById = new Map<ScenarioId, Scenario>();
    scenarios.forEach((s) => scenarioById.set(s.id, s));

    // Preserve active scenario when re-initializing for the same diagram
    // (e.g. AssistantPanelProvider remounting on sub-panel navigation)
    const isSameDiagram = currentDiagramId === diagramId;
    const activeStillExists = currentActiveId != null && scenarioById.has(currentActiveId);
    const preserveActive = isSameDiagram && activeStillExists;

    // On a fresh page load restore this tab's last active scenario: the draft
    // data survived in the Y.Doc, and losing the selection made it read as
    // deleted. Restored as VIEWING (never straight into edit mode).
    const restoredId = preserveActive
      ? null
      : chooseRestoredScenarioId(isSameDiagram ? null : diagramId, scenarios, scenarioById);
    if (!isSameDiagram) restorePendingForDiagramId = restoredId ? null : diagramId;

    set({
      diagramId,
      ydoc,
      scenarios,
      scenarioById,
      activeScenarioId: preserveActive ? currentActiveId : restoredId,
      activeScenarioRequestToken: preserveActive ? currentToken : null,
      scenarioState: preserveActive
        ? currentState
        : restoredId
          ? 'VIEWING_SCENARIO'
          : 'VIEWING_LIVE',
      connectorAuthoringMode: 'matrix_reporting',
      error: null,
    });

    detachScenarioObservers?.();
    detachScenarioObservers = null;

    // Observe scenario metadata and each aggregate contribution map. New
    // scenario maps are attached whenever scenario metadata changes.
    if (ydoc) {
      const scenariosMap = ydoc.getMap('scenarios');
      const contributionObservers = new Map<Y.Map<ScenarioAggregateContribution>, () => void>();
      const reload = () => {
        const updatedScenarios = loadScenariosFromYDoc(ydoc);
        const updatedMap = new Map<ScenarioId, Scenario>();
        updatedScenarios.forEach((s) => updatedMap.set(s.id, s));
        const restoringId = restorePendingForDiagramId === diagramId && get().activeScenarioId === null
          ? chooseRestoredScenarioId(diagramId, updatedScenarios, updatedMap)
          : null;
        if (restoringId) restorePendingForDiagramId = null;
        set({
          scenarios: updatedScenarios,
          scenarioById: updatedMap,
          ...(restoringId ? { activeScenarioId: restoringId, activeScenarioRequestToken: null, scenarioState: 'VIEWING_SCENARIO' as const } : {}),
        });
      };
      const attachContributionObservers = () => {
        scenariosMap.forEach((_value, key) => {
          const contributionMap = getScenarioContributionMap(ydoc, key);
          if (contributionObservers.has(contributionMap)) return;
          const observer = () => reload();
          contributionMap.observe(observer);
          contributionObservers.set(contributionMap, observer);
        });
      };
      const scenariosObserver = () => {
        attachContributionObservers();
        reload();
      };
      scenariosMap.observe(scenariosObserver);
      attachContributionObservers();
      detachScenarioObservers = () => {
        scenariosMap.unobserve(scenariosObserver);
        contributionObservers.forEach((observer, contributionMap) =>
          contributionMap.unobserve(observer));
      };
    }
  },

  // Create scenario
  createScenario: (name, description) => {
    const validation = CreateScenarioSchema.safeParse({ name, description });
    if (!validation.success) {
      console.warn('Invalid scenario input:', validation.error.issues);
    }

    const { ydoc, scenarios, scenarioById } = get();
    const id = createScenarioId(`scenario-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const delta = createEmptyDelta();

    const newScenario: Scenario = {
      id,
      name: name.trim(),
      description: description?.trim(),
      changes: [],
      delta,
      statusIndex: buildStatusIndex(delta),
      baseSnapshotHash: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'user',
    };

    const updatedScenarios = [...scenarios, newScenario];
    const updatedMap = new Map(scenarioById);
    updatedMap.set(id, newScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, newScenario);
    return newScenario;
  },

  // Rename scenario
  renameScenario: (id, name) => {
    const { ydoc, scenarios, scenarioById } = get();
    const scenarioId = createScenarioId(id);
    const scenario = scenarioById.get(scenarioId);
    if (!scenario) return;

    const updated = { ...scenario, name, updatedAt: new Date() };
    const updatedScenarios = scenarios.map((s) => (s.id === scenarioId ? updated : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(scenarioId, updated);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updated);
  },

  // Delete scenario
  deleteScenario: (id) => {
    const { ydoc, scenarios, scenarioById, activeScenarioId, scenarioState } = get();
    const scenarioId = createScenarioId(id);

    // Don't allow deletion while editing or committing
    if (scenarioState === 'EDITING_SCENARIO' || scenarioState === 'COMMITTING') {
      console.warn('Cannot delete scenario while editing or committing');
      return;
    }

    const updatedScenarios = scenarios.filter((s) => s.id !== scenarioId);
    const updatedMap = new Map(scenarioById);
    updatedMap.delete(scenarioId);

    set({
      scenarios: updatedScenarios,
      scenarioById: updatedMap,
      activeScenarioId: activeScenarioId === scenarioId ? null : activeScenarioId,
      scenarioState: activeScenarioId === scenarioId ? 'VIEWING_LIVE' : get().scenarioState,
    });
    if (activeScenarioId === scenarioId) rememberActiveScenario(get().diagramId, null);
    if (ydoc) deleteScenarioFromYDoc(ydoc, scenarioId);
  },

  // Set active scenario with request token for race prevention
  setActiveScenario: (id) => {
    const { scenarioState, scenarioById } = get();

    // Block switch during editing or committing
    if (scenarioState === 'EDITING_SCENARIO' || scenarioState === 'COMMITTING') {
      console.warn('Cannot switch scenarios while editing or committing. Exit edit mode first.');
      return Symbol('blocked');
    }

    const token = Symbol('scenario-request');
    const scenarioId = id ? createScenarioId(id) : null;

    // Validate scenario exists
    if (scenarioId && !scenarioById.has(scenarioId)) {
      console.warn('Scenario not found:', id);
      return token;
    }

    const newState: ScenarioLifecycleState = scenarioId ? 'VIEWING_SCENARIO' : 'VIEWING_LIVE';

    restorePendingForDiagramId = null;
    set({
      activeScenarioId: scenarioId,
      activeScenarioRequestToken: token,
      scenarioState: newState,
      error: null,
    });
    rememberActiveScenario(get().diagramId, scenarioId);

    return token;
  },

  // Enter edit mode
  enterEditMode: () => {
    const { scenarioState, activeScenarioId, scenarioById } = get();

    if (!activeScenarioId) {
      return failure('No active scenario to edit');
    }

    if (scenarioById.get(activeScenarioId)?.submissionState === 'submitted') {
      return failure('This scenario has been submitted for dashboard review');
    }

    if (!canTransition(scenarioState, 'EDITING_SCENARIO')) {
      return failure(`Cannot enter edit mode from state: ${scenarioState}`);
    }

    set({ scenarioState: 'EDITING_SCENARIO' });
    return successVoid();
  },

  setConnectorAuthoringMode: (connectorAuthoringMode) => {
    set({ connectorAuthoringMode });
  },

  // Exit edit mode
  exitEditMode: () => {
    const { scenarioState } = get();

    if (!canTransition(scenarioState, 'VIEWING_SCENARIO')) {
      return failure(`Cannot exit edit mode from state: ${scenarioState}`);
    }

    set({ scenarioState: 'VIEWING_SCENARIO', connectorAuthoringMode: 'matrix_reporting' });
    return successVoid();
  },

  // Add node to delta
  addNodeToDelta: (node) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to add nodes');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const updatedDelta: ScenarioDelta = {
      ...scenario.delta,
      additions: [...scenario.delta.additions, node],
    };

    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return success(node.id);
  },

  // Remove node from delta
  removeNodeFromDelta: (nodeId, originalData) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to remove nodes');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const removal: NodeRemoval = { nodeId, originalData };
    const updatedDelta: ScenarioDelta = {
      ...scenario.delta,
      removals: [...scenario.delta.removals, removal],
    };

    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  // Move node in delta
  moveNodeInDelta: (nodeId, oldManagerId, newManagerId) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to move nodes');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const move: NodeMove = { nodeId, oldManagerId, newManagerId };
    const updatedDelta: ScenarioDelta = {
      ...scenario.delta,
      moves: [...scenario.delta.moves, move],
    };

    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  // Update node in delta
  updateNodeInDelta: (nodeId, change) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to update nodes');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const update: NodeUpdate = { nodeId, change };
    const updatedDelta: ScenarioDelta = {
      ...scenario.delta,
      updates: [...scenario.delta.updates, update],
    };

    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  // Replace the active scenario delta as one command-owned transaction.
  // Used by domain editors that maintain a normalized aggregate (for example,
  // the full successor slate for one target role) rather than appending an
  // ambiguous sequence of edge additions and removals.
  replaceActiveScenarioDelta: (delta) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();
    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to replace the scenario delta');
    }
    if (!activeScenarioId) return failure('No active scenario');
    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) return failure('Active scenario not found');

    const updatedScenario: Scenario = {
      ...scenario,
      delta,
      statusIndex: buildStatusIndex(delta),
      updatedAt: new Date(),
    };
    const updatedScenarios = scenarios.map((item) =>
      item.id === activeScenarioId ? updatedScenario : item);
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);
    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);
    return successVoid();
  },

  upsertActiveScenarioAggregate: (contribution) => {
    const { scenarioState, activeScenarioId, ydoc } = get();
    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to update a scenario aggregate');
    }
    if (!activeScenarioId) return failure('No active scenario');
    if (!ydoc) return failure('Collaborative scenario storage is unavailable');
    if (!contribution.aggregateKey.trim()) return failure('Scenario aggregate key is required');

    const contributionMap = getScenarioContributionMap(ydoc, activeScenarioId as string);
    ydoc.transact(() => {
      contributionMap.set(contribution.aggregateKey, contribution);
    }, 'scenario-aggregate-upsert');

    const scenarios = loadScenariosFromYDoc(ydoc);
    const scenarioById = new Map<ScenarioId, Scenario>();
    scenarios.forEach((scenario) => scenarioById.set(scenario.id, scenario));
    set({ scenarios, scenarioById });
    return successVoid();
  },

  removeActiveScenarioAggregate: (aggregateKey) => {
    const { activeScenarioId, ydoc } = get();
    if (!activeScenarioId) return failure('No active scenario');
    if (!ydoc) return failure('Collaborative scenario storage is unavailable');
    const contributionMap = getScenarioContributionMap(ydoc, activeScenarioId as string);
    ydoc.transact(() => contributionMap.delete(aggregateKey), 'scenario-aggregate-remove');
    const scenarios = loadScenariosFromYDoc(ydoc);
    const scenarioById = new Map<ScenarioId, Scenario>();
    scenarios.forEach((scenario) => scenarioById.set(scenario.id, scenario));
    set({ scenarios, scenarioById });
    return successVoid();
  },

  getActiveScenarioAggregate: (aggregateKey) => {
    const { activeScenarioId, ydoc } = get();
    if (!activeScenarioId || !ydoc) return null;
    return getScenarioContributionMap(ydoc, activeScenarioId as string).get(aggregateKey) ?? null;
  },

  // Undo last delta change
  undoLastDeltaChange: () => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to undo');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    // Find the most recent change and remove it
    const { delta } = scenario;

    // Check which array has the most recent change (by position - last added)
    const arrays = [
      { name: 'additions', arr: delta.additions },
      { name: 'removals', arr: delta.removals },
      { name: 'moves', arr: delta.moves },
      { name: 'updates', arr: delta.updates },
      { name: 'edgeAdditions', arr: delta.edgeAdditions },
      { name: 'edgeRemovals', arr: delta.edgeRemovals },
    ].filter((a) => a.arr.length > 0);

    if (arrays.length === 0) {
      return failure('No changes to undo');
    }

    // Remove from the last non-empty array (simple LIFO within scenario)
    const lastArray = arrays[arrays.length - 1];
    const updatedDelta: ScenarioDelta = {
      ...delta,
      [lastArray.name]: lastArray.arr.slice(0, -1),
    };

    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  applyDeltaContributionToActiveScenario: (contribution) => {
    const { scenarioState, activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (scenarioState !== 'EDITING_SCENARIO') {
      return failure('Must be in editing mode to apply scenario contribution');
    }

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const typedContribution = contribution as CommandDeltaContribution<
      Record<string, unknown>,
      NodeUpdateChange
    >;
    const updatedDelta = mergeDeltas(scenario.delta, typedContribution);
    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  removeDeltaContributionFromActiveScenario: (contribution) => {
    const { activeScenarioId, scenarios, scenarioById, ydoc } = get();

    if (!activeScenarioId) {
      return failure('No active scenario');
    }

    const scenario = scenarioById.get(activeScenarioId);
    if (!scenario) {
      return failure('Active scenario not found');
    }

    const typedContribution = contribution as CommandDeltaContribution<
      Record<string, unknown>,
      NodeUpdateChange
    >;
    const updatedDelta = unmergeDeltas(scenario.delta, typedContribution);
    const updatedScenario: Scenario = {
      ...scenario,
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === activeScenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(activeScenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);

    return successVoid();
  },

  // Legacy: Add change (backward compatibility)
  addChange: (scenarioId, change) => {
    const { ydoc, scenarios, scenarioById } = get();
    const id = createScenarioId(scenarioId);
    const scenario = scenarioById.get(id);
    if (!scenario) return;

    const newChange: ScenarioChange = {
      ...change,
      id: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
    };

    // Also add to delta based on type
    const nodeId = createNodeId(change.nodeId);
    const baseDelta = scenario.delta;

    const getUpdatedDelta = (): ScenarioDelta => {
      switch (change.type) {
        case 'add':
          return {
            ...baseDelta,
            additions: [
              ...baseDelta.additions,
              {
                id: nodeId,
                type: DEFAULT_ORG_PERSON_NODE_TYPE,
                name: (change.data?.name as string) ?? 'Unknown',
                managerId: change.data?.managerId ? createManagerId(change.data.managerId as string) : undefined,
              },
            ],
          };
        case 'remove':
          return {
            ...baseDelta,
            removals: [
              ...baseDelta.removals,
              {
                nodeId,
                originalData: {
                  name: (change.data?.name as string) ?? 'Unknown',
                  type: DEFAULT_ORG_PERSON_NODE_TYPE,
                },
              },
            ],
          };
        case 'move':
          return {
            ...baseDelta,
            moves: [
              ...baseDelta.moves,
              {
                nodeId,
                oldManagerId: change.data?.oldParentId ? createManagerId(change.data.oldParentId as string) : null,
                newManagerId: change.data?.newParentId ? createManagerId(change.data.newParentId as string) : null,
              },
            ],
          };
        case 'modify':
          if (change.data?.field) {
            return {
              ...baseDelta,
              updates: [
                ...baseDelta.updates,
                {
                  nodeId,
                  change: {
                    field: change.data.field as 'name',
                    oldValue: (change.data.oldValue as string) ?? '',
                    newValue: (change.data.newValue as string) ?? '',
                  },
                },
              ],
            };
          }
          return baseDelta;
        default:
          return baseDelta;
      }
    };

    const updatedDelta = getUpdatedDelta();

    const updatedScenario: Scenario = {
      ...scenario,
      changes: [...scenario.changes, newChange],
      delta: updatedDelta,
      statusIndex: buildStatusIndex(updatedDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === id ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(id, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);
  },

  // Legacy: Remove change (backward compatibility)
  removeChange: (scenarioId, changeId) => {
    const { ydoc, scenarios, scenarioById } = get();
    const id = createScenarioId(scenarioId);
    const scenario = scenarioById.get(id);
    if (!scenario) return;

    const updatedScenario: Scenario = {
      ...scenario,
      changes: scenario.changes.filter((c) => c.id !== changeId),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === id ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(id, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap });
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);
  },

  getScenarios: () => get().scenarios,

  // Get scenario by ID
  getScenario: (id) => {
    const scenarioId = createScenarioId(id);
    return get().scenarioById.get(scenarioId);
  },

  // Get active scenario
  getActiveScenario: () => {
    const { activeScenarioId, scenarioById } = get();
    if (!activeScenarioId) return null;
    return scenarioById.get(activeScenarioId) ?? null;
  },

  // Get status index for active scenario
  getStatusIndex: () => {
    const { activeScenarioId, scenarioById } = get();
    if (!activeScenarioId) return null;
    const scenario = scenarioById.get(activeScenarioId);
    return scenario?.statusIndex ?? null;
  },

  // Apply scenario (returns changes for command execution)
  applyScenario: (id) => {
    const scenario = get().getScenario(id);
    if (!scenario) return [];
    return scenario.changes;
  },

  // Dashboard approval is deliberately separate from planning-chart rendering:
  // retain the authored delta as a read-only overlay so the submitted proposal
  // remains visible until the canonical chart is approved and refreshed.
  markScenarioSubmitted: (id, expectedUpdatedAt, serverDraft) => {
    const { ydoc, scenarios, scenarioById, activeScenarioId } = get();
    const scenarioId = createScenarioId(id);
    const scenario = scenarioById.get(scenarioId);
    if (!scenario || scenario.updatedAt.toISOString() !== expectedUpdatedAt) return false;

    const updatedScenario: Scenario = {
      ...scenario,
      submissionState: 'submitted',
      submittedScenarioId: serverDraft.scenarioId,
      submittedChangeSetId: serverDraft.changeSetId,
      submittedOperationalChangeSetId: serverDraft.operationalChangeSetId,
      updatedAt: new Date(),
    };
    const updatedScenarios = scenarios.map((item) =>
      item.id === scenarioId ? updatedScenario : item);
    const updatedMap = new Map(scenarioById);
    updatedMap.set(scenarioId, updatedScenario);
    const wasActive = activeScenarioId === scenarioId;

    set({
      scenarios: updatedScenarios,
      scenarioById: updatedMap,
      activeScenarioId: wasActive ? scenarioId : activeScenarioId,
      scenarioState: wasActive ? 'VIEWING_SCENARIO' : get().scenarioState,
    });
    if (wasActive) rememberActiveScenario(get().diagramId, scenarioId as string);
    if (ydoc) saveScenarioToYDoc(ydoc, updatedScenario);
    return true;
  },

  // Complete a client scenario only after its server-owned change has reached
  // a terminal applied/reconciled state (or the user explicitly discarded it).
  // The optimistic concurrency token prevents edits made while that request
  // was in flight from being cleared with the settlement.
  completeAppliedScenario: (id, expectedUpdatedAt) => {
    const { ydoc, scenarios, scenarioById, activeScenarioId, scenarioState } = get();
    const scenarioId = createScenarioId(id);
    const scenario = scenarioById.get(scenarioId);
    if (!scenario || scenario.updatedAt.toISOString() !== expectedUpdatedAt) return false;

    const emptyDelta = createEmptyDelta();
    const updatedScenario: Scenario = {
      ...scenario,
      changes: [],
      delta: emptyDelta,
      statusIndex: buildStatusIndex(emptyDelta),
      submissionState: undefined,
      submittedScenarioId: undefined,
      submittedChangeSetId: undefined,
      submittedOperationalChangeSetId: undefined,
      updatedAt: new Date(),
    };
    const updatedScenarios = scenarios.map((item) =>
      item.id === scenarioId ? updatedScenario : item);
    const updatedMap = new Map(scenarioById);
    updatedMap.set(scenarioId, updatedScenario);
    const wasActive = activeScenarioId === scenarioId;

    set({
      scenarios: updatedScenarios,
      scenarioById: updatedMap,
      activeScenarioId: wasActive ? null : activeScenarioId,
      activeScenarioRequestToken: wasActive ? null : get().activeScenarioRequestToken,
      scenarioState: wasActive ? 'VIEWING_LIVE' : scenarioState,
    });
    if (wasActive) rememberActiveScenario(get().diagramId, null);
    if (ydoc) {
      const aggregateMap = getScenarioContributionMap(ydoc, scenarioId as string);
      ydoc.transact(() => aggregateMap.clear(), 'scenario-aggregate-apply-complete');
      saveScenarioToYDoc(ydoc, updatedScenario);
    }
    return true;
  },

  // Discard scenario changes
  discardScenario: (id) => {
    const { ydoc, scenarios, scenarioById, scenarioState } = get();
    const scenarioId = createScenarioId(id);
    const scenario = scenarioById.get(scenarioId);
    if (!scenario) return;

    // Exit edit mode if discarding active scenario
    const newState = scenarioState === 'EDITING_SCENARIO' ? 'VIEWING_SCENARIO' : scenarioState;

    const emptyDelta = createEmptyDelta();
    const updatedScenario: Scenario = {
      ...scenario,
      changes: [],
      delta: emptyDelta,
      statusIndex: buildStatusIndex(emptyDelta),
      updatedAt: new Date(),
    };

    const updatedScenarios = scenarios.map((s) => (s.id === scenarioId ? updatedScenario : s));
    const updatedMap = new Map(scenarioById);
    updatedMap.set(scenarioId, updatedScenario);

    set({ scenarios: updatedScenarios, scenarioById: updatedMap, scenarioState: newState });
    if (ydoc) {
      const aggregateMap = getScenarioContributionMap(ydoc, scenarioId as string);
      ydoc.transact(() => aggregateMap.clear(), 'scenario-aggregate-discard');
      saveScenarioToYDoc(ydoc, updatedScenario);
    }
  },
}));

// =============================================================================
// Selector Hooks for Optimized Re-renders
// =============================================================================

/** Get scenario count without subscribing to all scenario data */
export const useScenarioCount = () => useScenariosStore((s) => s.scenarios.length);

/** Get active scenario ID only */
export const useActiveScenarioId = () => useScenariosStore((s) => s.activeScenarioId);

/** Get lifecycle state only */
export const useScenarioLifecycleState = () => useScenariosStore((s) => s.scenarioState);

/** Check if currently editing */
export const useIsEditingScenario = () => useScenariosStore((s) => s.scenarioState === 'EDITING_SCENARIO');

/** Get status index for rendering */
export const useScenarioStatusIndex = () => useScenariosStore((s) => s.getStatusIndex());
